import React, {
  FocusEvent,
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react'
import { InputAdornmentProps, InputBaseProps } from '@mui/material'

import { Field } from '../../types'
import {
  CurrencySymbolPositions,
  getCurrencySymbol,
  getCurrencySymbolPosition,
  getDecimalSeparator,
  getThousandSeparator,
} from '../../utils/numberUtils'
import MoneyInput from '../form-inputs/MoneyInput'
import PuiInputAdornment from './PuiInputAdornment'
import PuiTextField, { PuiTextFieldProps } from './PuiTextField'

export type CurrencyTextFieldProps = PuiTextFieldProps & {
  readonly ignoreCursorSetting?: boolean
  readonly max?: number | string
  readonly min?: number | string
  readonly noLonelyAdornment?: boolean
  readonly onChange?: (event: FocusEvent<HTMLInputElement>) => void
  readonly selectZeroOnFocus?: boolean
}

const CurrencyTextField = forwardRef<HTMLDivElement, CurrencyTextFieldProps>(
  function CurrencyTextField(
    {
      noLonelyAdornment = false,
      value: initialValue,
      field,
      min,
      max,
      InputProps = {},
      inputProps = {},
      selectZeroOnFocus = false,
      ignoreCursorSetting = false,
      onChange,
      ...rest
    },
    ref,
  ) {
    const innerRef = useRef<HTMLDivElement>(null)
    useImperativeHandle(ref, () => innerRef.current as HTMLDivElement)
    const [cursor, setCursor] = useState(0)
    const value = field?.value ?? initialValue

    const isBeforeAdornmentPosition: boolean =
      getCurrencySymbolPosition() === CurrencySymbolPositions.BEFORE
    const inputAdornmentPositionProp: keyof Pick<
    InputBaseProps,
    'startAdornment' | 'endAdornment'
    > = isBeforeAdornmentPosition ? 'startAdornment' : 'endAdornment'
    const adornmentInnerPosition: InputAdornmentProps['position'] =
      isBeforeAdornmentPosition ? 'start' : 'end'

    useEffect(() => {
      if (!ignoreCursorSetting) {
        const input = innerRef.current?.firstElementChild
          ?.lastElementChild as HTMLInputElement
        if (input) {
          input?.setSelectionRange(cursor, cursor)
        }
      }
    }, [
      innerRef.current?.firstElementChild?.lastElementChild,
      cursor,
      value,
      ignoreCursorSetting,
    ])

    const handleCursor = (e: FocusEvent<HTMLInputElement>) => {
      if (field) {
        field.set(e)
      }

      if (onChange) {
        onChange(e)
      }

      if (!ignoreCursorSetting) {
        setCursor(e?.currentTarget?.selectionStart || 0)
      }
    }
    return (
      <PuiTextField
        shrink
        InputProps={{
          ...InputProps,
          inputComponent: MoneyInput,
          inputProps: {
            ...inputProps,
            thousandSeparator: getThousandSeparator(),
            decimalSeparator: getDecimalSeparator(),
            maxLength: 100,
            min,
            max,
          },
          [inputAdornmentPositionProp]: (
            <PuiInputAdornment
              hidden={noLonelyAdornment && !value}
              position={adornmentInnerPosition}
            >
              {getCurrencySymbol()}
            </PuiInputAdornment>
          ),
        }}
        field={
          (field
            ? {
              ...field,
              set: handleCursor,
            }
            : {}) as Field
        }
        ref={innerRef}
        type="text"
        value={value}
        onChange={handleCursor}
        onFocus={(event: FocusEvent<HTMLInputElement>) =>
          selectZeroOnFocus &&
          (value === 0 || value === '0.00') &&
          event.target.select()
        }
        {...rest}
      />
    )
  },
)

export default CurrencyTextField
